import { ApolloCache, DocumentNode, TypedDocumentNode } from '@apollo/client';
import { useCallback } from 'react';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { useToasts } from '../../../../../../context/Toast';
import { useApolloCache } from '../../../../hooks/useApolloCache';
import {
  AddSkillToProjectInput,
  RemoveSkillFromProjectInput,
  UpdateProjectSkillRateInput,
  UpdateProjectSkillRateResponseFragment,
  UpdateProjectSkillRateResponseFragmentDoc,
  UpdateProjectSkillTopInput,
  UpdateProjectSkillTopResponseFragment,
  UpdateProjectSkillTopResponseFragmentDoc,
  useAddProjectSkillMutation,
  useRemoveProjectSkillMutation,
  useUpdateProjectSkillRateMutation,
  useUpdateProjectSkillTopMutation,
} from '../../../../__generated__/graphql';

// Changing the project's required skills changes who matches. matchingEmployees
// is computed server-side (Apex) and can't be recomputed in the cache, so refetch
// the panel's query whenever a required skill is added, removed, or re-rated.
const REFETCH_ON_SKILL_CHANGE = 'ProjectMatchingEmployees';

export const useAddProjectSkill = () => {
  const [addProjectSkillMutation, { loading }] = useAddProjectSkillMutation();
  const errorHandler = useMutationErrorHandler();

  const { addSuccess } = useToasts();
  const t = useNamespacedTranslation('skills.projectSkills');

  const addProjectSkill = useCallback(
    (input: AddSkillToProjectInput): Promise<boolean> => {
      return addProjectSkillMutation({
        variables: { input },
        refetchQueries: [REFETCH_ON_SKILL_CHANGE],
      })
        .then((data) => {
          if (data.data?.projectSkills.addSkill.error) {
            throw new Error(data.data?.projectSkills.addSkill.error);
          }
          addSuccess(t(`toastAddedSuccess`));
          return true;
        })
        .catch((e) => {
          errorHandler(e);
          return false;
        });
    },
    [addProjectSkillMutation, t, errorHandler, addSuccess],
  );

  return { addProjectSkill, inProgress: loading };
};

export const useRemoveProjectSkill = () => {
  const [removeProjectSkillMutation, { loading }] =
    useRemoveProjectSkillMutation();
  const errorHandler = useMutationErrorHandler();

  const { addSuccess } = useToasts();
  const t = useNamespacedTranslation('skills.projectSkills');

  const removeProjectSkill = useCallback(
    (input: RemoveSkillFromProjectInput): Promise<boolean> => {
      return removeProjectSkillMutation({
        variables: { input },
        refetchQueries: [REFETCH_ON_SKILL_CHANGE],
      })
        .then((data) => {
          if (data.data?.projectSkills.removeSkill.error) {
            throw new Error(data.data?.projectSkills.removeSkill.error);
          }
          addSuccess(t(`toastRemovedSuccess`));
          return true;
        })
        .catch((e) => {
          errorHandler(e);
          return false;
        });
    },
    [removeProjectSkillMutation, t, errorHandler, addSuccess],
  );

  return { removeProjectSkill, inProgress: loading };
};

export const useUpdateProjectSkillTop = () => {
  const cache = useApolloCache();
  const [updateProjectSkillTopMutation, { loading }] =
    useUpdateProjectSkillTopMutation({
      optimisticResponse: ({ input }) => {
        const fragment = readUpdateSkillTopResponseFragment(cache, input.id);
        return {
          projectSkills: {
            updateSkillTop: {
              error: null,
              record: { ...fragment, flair__Top__c: input.top },
            },
          },
        };
      },
    });
  const errorHandler = useMutationErrorHandler();

  const updateProjectSkillTop = useCallback(
    (input: UpdateProjectSkillTopInput) => {
      return updateProjectSkillTopMutation({
        variables: { input },
      })
        .then((data) => {
          if (data.data?.projectSkills.updateSkillTop.error) {
            throw new Error(data.data?.projectSkills.updateSkillTop.error);
          }
        })
        .catch(errorHandler);
    },
    [updateProjectSkillTopMutation, errorHandler],
  );

  return { updateProjectSkillTop, inProgress: loading };
};

export const useUpdateProjectSkillRate = () => {
  const cache = useApolloCache();

  const [updateProjectSkillRateMutation, { loading }] =
    useUpdateProjectSkillRateMutation({
      optimisticResponse: ({ input }) => {
        const fragment = readUpdateSkillRateResponseFragment(cache, input.id);
        const updated = {
          ...fragment,
          flair__Rate__c: input.rate,
        };
        if (input.rate === null) {
          updated.flair__Top__c = false;
        }
        return {
          projectSkills: {
            updateSkillRate: {
              error: null,
              record: updated,
            },
          },
        };
      },
    });
  const errorHandler = useMutationErrorHandler();

  const updateProjectSkillRate = useCallback(
    (input: UpdateProjectSkillRateInput) => {
      return updateProjectSkillRateMutation({
        variables: { input },
        refetchQueries: [REFETCH_ON_SKILL_CHANGE],
      })
        .then((data) => {
          if (data.data?.projectSkills.updateSkillRate.error) {
            throw new Error(data.data?.projectSkills.updateSkillRate.error);
          }
        })
        .catch(errorHandler);
    },
    [updateProjectSkillRateMutation, errorHandler],
  );

  return { updateProjectSkillRate, inProgress: loading };
};

function readProjectSkillFragment<TFragment>(
  cache: ApolloCache<object>,
  id: string,
  fragment: DocumentNode | TypedDocumentNode,
  fragmentName: string,
): TFragment {
  const projectSkillFragment: TFragment | null = cache.readFragment<TFragment>({
    id: cache.identify({
      __typename: 'ProjectSkillAssociation',
      Id: id,
    }),
    fragment,
    fragmentName,
  });

  if (projectSkillFragment === null) {
    throw new Error('Can not find fragment in cache');
  }
  return projectSkillFragment;
}

function readUpdateSkillTopResponseFragment(
  cache: ApolloCache<object>,
  id: string,
): UpdateProjectSkillTopResponseFragment {
  return readProjectSkillFragment<UpdateProjectSkillTopResponseFragment>(
    cache,
    id,
    UpdateProjectSkillTopResponseFragmentDoc,
    'UpdateProjectSkillTopResponse',
  );
}

function readUpdateSkillRateResponseFragment(
  cache: ApolloCache<object>,
  id: string,
): UpdateProjectSkillRateResponseFragment {
  return readProjectSkillFragment<UpdateProjectSkillRateResponseFragment>(
    cache,
    id,
    UpdateProjectSkillRateResponseFragmentDoc,
    'UpdateProjectSkillRateResponse',
  );
}
